import { LeaveDurationTypeEnum } from '@cyberlabrs/helpers';
import { IDateRange } from '../../../../util/date/dateHelper';
import { MedicalLeaveRequestBaseResponseDto } from '../../../../commonTypes/user.types';
import { MedicalLeaveCellHeadFlexible } from '../../../../commonTypes/calendar.types';
import { HolidayDto } from '../../../../commonTypes/organization.types';
import {
    useHttpCreateMedicalLeaveRequest,
    useHttpEditMedicalLeaveRequest,
    useHttpEndMedicalLeaveRequest,
    useHttpGetDisabledDays,
    useHttpGetHolidaysByBranchOnMount,
    useHttpGetMedicalLeaveByOrganizationOnMount,
    useHttpShowUserDocument,
    useOrganizationId,
} from '../../../../hooks';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { AuthState } from '../../../../store/slices/auth.slice';
import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import REQUEST_SCHEMA from '../requestSchema';
import { Button, Dropdown, IconButton, Img, MixerLoader, Modal, TextAreaFormik, YesNo } from '../../../Common';
import { LeaveRequestStatus, LeaveRequestType } from '../../../../commonTypes/leaveRequest.types';
import DataRangePicker from '../../../DateRangePicker';

export interface IRequestMedicalLeaveState {
    dates: IDateRange;
    description: string;
    leaveDurationType: LeaveDurationTypeEnum;
    leaveType?: { label: string; value: string };
    leaveDescriptionRequired?: boolean;
}

interface IRequestMedicalLeave {
    close: () => void;
    createMedicalLeaveRequestOnSuccess?: (request: MedicalLeaveRequestBaseResponseDto, holidays: HolidayDto[]) => void;
    predefinedDates?: IDateRange;
    isEndMedicalLeave?: boolean;
    medicalLeaveToEnd?: MedicalLeaveCellHeadFlexible;
    heading?: string;
    isEditMedical?: boolean;
    isCreate?: boolean;
    skipDisabledDays?: boolean;
}

const RequestMedicalLeave = ({
    close,
    createMedicalLeaveRequestOnSuccess,
    predefinedDates,
    isEndMedicalLeave = false,
    medicalLeaveToEnd,
    heading,
    isEditMedical = false,
    isCreate = true,
    skipDisabledDays = false,
}: IRequestMedicalLeave) => {
    const { t } = useTranslation();
    const { userSub } = useParams();
    const organizationId = useOrganizationId();
    const { medicalLeave } = useHttpGetMedicalLeaveByOrganizationOnMount(organizationId);
    const auth = useSelector<RootState, AuthState>(state => state.auth);

    const { editMedicalLeaveRequest } = useHttpEditMedicalLeaveRequest();

    const { data: holidays, isReady: holidaysAreReady } = useHttpGetHolidaysByBranchOnMount(auth.value.branchId);
    const [files, setFiles] = useState<File[]>([]);
    const [isMedicalLeaveCreateModalVisible, setIsMedicalLeaveCreateModalVisible] = useState<boolean>(false);
    const linkRef = useRef<HTMLAnchorElement>(null);
    const { showUserDocument } = useHttpShowUserDocument();

    const httpDisabledDays = useHttpGetDisabledDays();

    const disabledDays = skipDisabledDays ? [] : httpDisabledDays.disabledDays;
    const getDisabledDaysWithMessage = skipDisabledDays ? () => {} : httpDisabledDays.getDisabledDaysWithMessage;
    const { createMedicalLeaveRequest } = useHttpCreateMedicalLeaveRequest();
    const { endMedicalLeaveRequest } = useHttpEndMedicalLeaveRequest();

    const getInitialValues = (): IRequestMedicalLeaveState => {
        if (isEditMedical && medicalLeaveToEnd) {
            const startDateValue = medicalLeaveToEnd.fromDate || medicalLeaveToEnd.startDate;
            const endDateValue = medicalLeaveToEnd.toDate || medicalLeaveToEnd.endDate;

            let startDate: DateTime | null = startDateValue ? DateTime.fromISO(startDateValue) : null;
            let endDate: DateTime | null = null;
            if (endDateValue && endDateValue !== 'Present' && endDateValue.trim() !== '') {
                endDate = DateTime.fromISO(endDateValue);
                if (!endDate.isValid) endDate = null;
            }

            if (startDate && !startDate.isValid) startDate = null;

            return {
                dates: { startDate, endDate },
                description: medicalLeaveToEnd.description || '',
                leaveDurationType: LeaveDurationTypeEnum.FULL_DAY,
                leaveType: { label: 'Medical Leave', value: LeaveRequestType.MEDICAL_LEAVE },
                leaveDescriptionRequired: medicalLeave?.reqDescriptionRequired,
            };
        }
        if (isEndMedicalLeave && medicalLeaveToEnd) {
            const startDate = medicalLeaveToEnd.startDate ? DateTime.fromISO(medicalLeaveToEnd.startDate) : null;
            const endDate = medicalLeaveToEnd.endDate ? DateTime.fromISO(medicalLeaveToEnd.endDate) : null;

            return {
                dates: { startDate, endDate: endDate || null },
                description: medicalLeaveToEnd.description || '',
                leaveDurationType: LeaveDurationTypeEnum.FULL_DAY,
                leaveType: { label: 'Medical Leave', value: LeaveRequestType.MEDICAL_LEAVE },
                leaveDescriptionRequired: medicalLeave?.reqDescriptionRequired,
            };
        }

        return {
            dates: predefinedDates || { startDate: null, endDate: null },
            description: '',
            leaveDurationType: LeaveDurationTypeEnum.FULL_DAY,
            leaveType: { label: 'Medical Leave', value: LeaveRequestType.MEDICAL_LEAVE },
            leaveDescriptionRequired: medicalLeave?.reqDescriptionRequired,
        };
    };

    const context = useFormik<IRequestMedicalLeaveState>({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        validationSchema: REQUEST_SCHEMA(t),
        onSubmit: v => {
            if (isEndMedicalLeave || isEditMedical) {
                requestMedicalLeave(v);
            } else {
                setIsMedicalLeaveCreateModalVisible(true);
            }
        },
    });

    useEffect(() => {
        if (isEditMedical && medicalLeaveToEnd) {
            context.resetForm({ values: getInitialValues() });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [medicalLeaveToEnd, isEditMedical]);

    const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles) return;

        const newFiles = Array.from(selectedFiles);
        setFiles(prev => {
            const existingFileNames = new Set(prev.map(f => f.name));
            const uniqueNewFiles = newFiles.filter(f => !existingFileNames.has(f.name));
            return [...prev, ...uniqueNewFiles];
        });

        event.target.value = '';
    };

    const handleShowDocument = (file: File) => {
        const url = URL.createObjectURL(file);
        window.open(url, '_blank');
        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 100);
    };

    const handleDownloadDocument = (file: File) => {
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 100);
    };

    const handleShowExistingDocument = (id: string, download: boolean) => {
        showUserDocument(id, download, linkRef);
    };

    const fetchDisabledDays = (from: string, to: string) => {
        void getDisabledDaysWithMessage(from, to);
    };

    const requestMedicalLeave = (values: IRequestMedicalLeaveState) => {
        if (isEndMedicalLeave && medicalLeaveToEnd) {
            if (!values.dates.endDate) return;

            void endMedicalLeaveRequest(medicalLeaveToEnd.id, values.dates.endDate.toISODate(), {
                onSuccess: res => {
                    createMedicalLeaveRequestOnSuccess?.(res, holidays);
                    close();
                    window.location.reload();
                },
            });
        } else if (isEditMedical && medicalLeaveToEnd) {
            const body = {
                id: medicalLeaveToEnd.id,
                startDate: values.dates.startDate?.toISODate(),
                endDate: values.dates.endDate ? values.dates.endDate.toISODate() : null,
                description: values.description || undefined,
                files: files,
            };

            void editMedicalLeaveRequest(body, {
                onSuccess: res => {
                    createMedicalLeaveRequestOnSuccess?.(res, holidays);
                    close();
                    window.location.reload();
                },
                onError: error => {
                    console.error('Edit failed:', error);
                },
            });
        } else if (values.dates.startDate) {
            const body = {
                startDate: values.dates.startDate.toISODate(),
                endDate: values.dates.endDate ? values.dates.endDate.toISODate() : undefined,
                description: values.description || undefined,
                files: files,
                status: LeaveRequestStatus.PENDING,
                userSub: isCreate ? userSub ?? auth.value.userSub : auth.value.userSub,
            };

            void createMedicalLeaveRequest(body, {
                onSuccess: res => {
                    createMedicalLeaveRequestOnSuccess?.(res, holidays);
                    close();
                    window.location.reload();
                },
            });
        }
    };

    const checkIfButtonIsDisabled = (): boolean => {
        const { startDate, endDate } = context.values.dates;

        if (isEndMedicalLeave) {
            return !endDate;
        }

        if (!startDate) return true;
        if (Object.keys(context.errors).length > 0) return true;

        if (!isEditMedical) {
            const end = endDate ?? startDate;
            const interval = startDate.until(end);

            const allDisabledDates = disabledDays.flatMap(d => d.dates.map(date => DateTime.fromISO(date)));
            const overlaps = interval.splitBy({ day: 1 }).some(d => allDisabledDates.some(dd => dd.hasSame(d.start, 'day')));
            if (overlaps) return true;
        }

        if (!isEndMedicalLeave && !isEditMedical) {
            const descriptionRequired = !!medicalLeave?.reqDescriptionRequired;
            const documentRequired = !!medicalLeave?.documentRequired;
            const isDescriptionEmpty = descriptionRequired && !context.values.description?.trim();
            const areDocumentsMissing = documentRequired && files.length === 0;
            if (isDescriptionEmpty || areDocumentsMissing) return true;
        }

        return false;
    };

    const defaultHeading = isEndMedicalLeave ? 'End Medical Leave Request' : 'Medical Leave Request';
    const buttonText = isEndMedicalLeave ? 'End Medical Leave' : isEditMedical ? 'Update Medical Leave' : 'Submit';

    return (
        <div className="request-leave-n">
            <h3 className="request-leave-n__header">{heading ?? (isEditMedical ? 'Edit Medical Leave' : defaultHeading)}</h3>
            <form className="request-leave-n__form" onSubmit={context.handleSubmit}>
                <div className="request-leave-n__inputs">
                    <div className="request-leave-n__main">
                        <Dropdown
                            label="Policy"
                            menuItems={[{ value: LeaveRequestType.MEDICAL_LEAVE, label: 'Medical Leave' }]}
                            selected={{ label: 'Medical Leave', value: LeaveRequestType.MEDICAL_LEAVE }}
                            onChange={() => {}}
                            testId="policy-dropdown"
                            disabled={true}
                        />

                        <TextAreaFormik
                            id="description"
                            label="Description"
                            placeholder="Add description"
                            isRequired={medicalLeave?.reqDescriptionRequired ?? false}
                            context={context}
                            testId="test-description"
                            disabled={isEndMedicalLeave}
                        />
                        {!isEndMedicalLeave && (
                            <>
                                {isEditMedical &&
                                    medicalLeaveToEnd?.medicalLeaveRequestDocument &&
                                    medicalLeaveToEnd.medicalLeaveRequestDocument.length > 0 && (
                                        <div className="request-leave-n__existing-documents">
                                            <label className="text-area__label paragraph-4">Existing documents</label>
                                            <div className="request-leave-n__documents__selected-files">
                                                {medicalLeaveToEnd.medicalLeaveRequestDocument.map((doc, index) => (
                                                    <div key={index} className="request-leave-n__documents__file-item">
                                                        <span className="request-leave-n__documents__file-name paragraph-4">{doc.name}</span>
                                                        <div className="request-leave-n__documents__upload-wrapper">
                                                            <Img
                                                                src="/svg/eye.svg"
                                                                alt="Preview"
                                                                className="request-leave-n__documents__file-action-icon"
                                                                onClick={() => handleShowExistingDocument(doc.id, false)}
                                                            />
                                                            <Img
                                                                src="/svg/document-download.svg"
                                                                alt="Download"
                                                                className="request-leave-n__documents__file-action-icon"
                                                                onClick={() => handleShowExistingDocument(doc.id, true)}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                <label className="text-area__label paragraph-4">
                                    {isEditMedical ? 'Upload additional documents' : 'Attach documents'}
                                    {medicalLeave?.documentRequired && !isEditMedical && <span style={{ color: 'red' }}>*</span>}
                                </label>
                                <div className="request-leave-n__documents__upload-wrapper">
                                    <label htmlFor="documents-upload">
                                        <div>
                                            <IconButton
                                                iconUrl="/svg/document-upload.svg"
                                                iconAlt="upload-document"
                                                variant="outline"
                                                onClick={() => document.getElementById('documents-upload')?.click()}
                                                type="button"
                                            />
                                        </div>
                                        <input
                                            id="documents-upload"
                                            type="file"
                                            accept=".pdf"
                                            multiple
                                            onChange={handleFilesChange}
                                            style={{ display: 'none' }}
                                        />
                                    </label>

                                    <div className="request-leave-n__documents__selected-files">
                                        {files.map((file, index) => (
                                            <div key={index} className="request-leave-n__documents__file-item">
                                                <span className="request-leave-n__documents__file-name paragraph-4">{file.name}</span>
                                                <div className="request-leave-n__documents__upload-wrapper">
                                                    <Img
                                                        src="/svg/eye.svg"
                                                        alt="Preview"
                                                        className="request-leave-n__documents__file-action-icon"
                                                        onClick={() => handleShowDocument(file)}
                                                    />
                                                    <Img
                                                        src="/svg/document-download.svg"
                                                        alt="Download"
                                                        className="request-leave-n__documents__file-action-icon"
                                                        onClick={() => handleDownloadDocument(file)}
                                                    />
                                                    <Img
                                                        src="/svg/close-x-blue.svg"
                                                        alt="Delete"
                                                        className="request-leave-n__documents__file-action-icon"
                                                        onClick={() => setFiles(prev => prev.filter((_, i) => i !== index))}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {!holidaysAreReady ? (
                        <MixerLoader position="centerOnParent" size="medium" />
                    ) : (
                        <DataRangePicker
                            values={context.values}
                            disabledDays={isEndMedicalLeave ? [] : disabledDays}
                            setFieldValue={(field, value) => {
                                if (field === 'dates') {
                                    context.setFieldValue('dates', { startDate: value.startDate, endDate: value.endDate });
                                } else {
                                    context.setFieldValue(field, value);
                                }
                            }}
                            onPeriodChange={fetchDisabledDays}
                            holidays={holidays}
                            initialDate={context.values.dates.startDate}
                            isEndMedicalLeave={isEndMedicalLeave}
                            fixedStartDate={isEndMedicalLeave && medicalLeaveToEnd ? medicalLeaveToEnd.startDate : undefined}
                        />
                    )}
                </div>

                <div className="request-leave-n__buttons">
                    <Button text="Cancel" variant="text" onClick={close} color="blue" />
                    <Button isSubmit text={buttonText} disabled={checkIfButtonIsDisabled()} testId="submit-button" />
                </div>
            </form>

            <Modal isVisible={isMedicalLeaveCreateModalVisible} closeModal={() => setIsMedicalLeaveCreateModalVisible(false)}>
                <YesNo
                    title={t('medicalLeaveRequest.create')}
                    description={`${t('medicalLeaveRequest.createDescription')}`}
                    onYes={() => {
                        requestMedicalLeave(context.values);
                        setIsMedicalLeaveCreateModalVisible(false);
                    }}
                    onNo={() => setIsMedicalLeaveCreateModalVisible(false)}
                />
            </Modal>
            <a ref={linkRef} style={{ display: 'none' }}></a>
        </div>
    );
};

export default RequestMedicalLeave;
